import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ErrorMessage } from '@as-react/common-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { Banner, Box, ButtonAlpha, Grid, Stack } from '@yonderland/andes-react';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useDeliveryState } from '../../../../context/CheckoutDeliveryContext';
import { AddressFields } from '../../../form/address-fields/AddressFields';
import { emptyAddressFields, getAddressValidationSchema, prepareAddressByCountry, } from '../../../form/address-fields/addressFields.utils';
import { useScrollToFirstFormError } from '../../../form/hooks/useScrollToFirstFormError';
import { getCustomerDetailsValidationSchema } from '../../../form/registration-fields/CustomerDetailsFields.utils';
import { AddressPreview } from '../AddressPreview';
import { CustomerDetailsFields } from '../CustomerDetailsFields';
export const HomeDeliveryAddressForm = ({ address, isEditing = false, checked = false, onSubmit, onEdit, onCancel, isPending, error, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.checkout);
    const [{ info }] = useDeliveryState();
    const customerValidationSchema = getCustomerDetailsValidationSchema({ t });
    const validationSchema = customerValidationSchema.concat(getAddressValidationSchema({ country: info.country.toUpperCase(), t, isOptional: false }));
    const formProps = useForm({
        defaultValues: Object.assign(Object.assign({}, (address || emptyAddressFields)), { idCountry: info.idCountry, country: info.country }),
        mode: 'onTouched',
        shouldFocusError: true,
        resolver: yupResolver(validationSchema),
    });
    const [isValidAddress, setIsValidAddress] = useState(true);
    useEffect(() => {
        setIsValidAddress(Object.keys(formProps.formState.errors).length === 0);
    }, [formProps.formState.errors]);
    useEffect(() => {
        if (checked && address) {
            formProps.trigger();
        }
    }, [checked, address]);
    const isFormInvalid = checked && !isValidAddress;
    const showAddressForm = isEditing || isFormInvalid;
    const hasScrolled = useRef(false);
    useScrollToFirstFormError(formProps.formState, isEditing && isFormInvalid);
    useEffect(() => {
        if (checked && address && !isEditing && isFormInvalid) {
            onEdit === null || onEdit === void 0 ? void 0 : onEdit(address.deliveryAddressId);
            if (!hasScrolled.current) {
                hasScrolled.current = true;
            }
        }
    }, [address, isEditing, isFormInvalid, checked]);
    const handleSubmit = (data) => {
        const preparedAddress = prepareAddressByCountry(data, '');
        onSubmit(preparedAddress);
    };
    const getButtonState = () => (isPending ? 'loading' : 'interactive');
    const handleFormSubmit = () => {
        formProps.handleSubmit(handleSubmit)();
    };
    const handleEditAddress = () => {
        if (!address) {
            return;
        }
        onEdit === null || onEdit === void 0 ? void 0 : onEdit(address.deliveryAddressId);
    };
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(onSubmit), noValidate: true, name: "home-delivery-address-form", children: showAddressForm ? (_jsx(Box, { backgroundColor: "quiet", padding: "4", children: _jsxs(Stack, { wrap: "wrap", align: "start", justify: "between", gap: "6", children: [_jsx(CustomerDetailsFields, {}), _jsx(Grid, { fullWidth: true, children: _jsx(AddressFields, { country: info.country, hasInitialAddress: !!address, isRequired: true }) }), error && _jsx(Banner, { text: t(ErrorMessage.GENERAL_ACCOUNT), status: "error" }), _jsxs(Stack, { wrap: "wrap", gap: "3", children: [_jsx(ButtonAlpha, { state: getButtonState(), theme: "branded", text: address ? t('checkout.delivery.flyout.update') : t('checkout.delivery.delivery.address.save'), onClick: handleFormSubmit }), !address && (_jsx(ButtonAlpha, { theme: "branded", variant: "secondary", text: t('checkout.delivery.delivery.address.cancel'), onClick: onCancel }))] })] }) })) : (checked && (_jsx(AddressPreview, { isValid: isValidAddress, address: address, testId: "hd_address_card", showTitle: false, onEdit: handleEditAddress }))) }) })));
};
